import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Reveal, MaskedLine, Overline, EASE } from "@/components/motion";
import Marquee from "@/components/Marquee";
import ProjectCard from "@/components/ProjectCard";
import Placeholder from "@/components/Placeholder";

const SERVICES = [
  "Brand Design",
  "Presentations & Proposals",
  "Reports & Publications",
  "Digital, Social & Web Visuals",
  "Motion & Video",
  "Design Direction",
];

const PRINCIPLES = [
  {
    n: "01",
    title: "Clarity before decoration",
    body: "Every design decision starts with what the audience needs to understand. If an element doesn't serve the argument, it goes.",
  },
  {
    n: "02",
    title: "Systems over one-offs",
    body: "I build toolkits, templates and rules that teams can reuse — so the hundredth deck looks as considered as the first.",
  },
  {
    n: "03",
    title: "Credibility is designed",
    body: "In consulting, trust is won in the first ten seconds. Typography, grid and restraint do the persuading before a word is read.",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [settings, setSettings] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.15]);

  useEffect(() => {
    api.get("/projects", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[94vh] flex items-center pt-16 overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div className="lg:col-span-8" style={{ opacity: fade }}>
            <MaskedLine delay={0.1}>
              <Overline>Senior Visual &amp; Brand Designer — Delhi / Gurugram</Overline>
            </MaskedLine>
            <h1 className="mt-8 font-serif tracking-tight leading-[0.95] text-6xl sm:text-8xl lg:text-[9rem]" data-testid="hero-name">
              <MaskedLine delay={0.25}>Twinkle</MaskedLine>
              <MaskedLine delay={0.4}>
                <span className="italic text-accent">Rajan</span>
              </MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
              className="mt-10 max-w-xl text-lg sm:text-xl leading-relaxed text-foreground/75"
              data-testid="hero-pitch"
            >
              I turn complex consulting ideas into clear, credible design: brand systems,
              proposals, publications and digital experiences.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1, ease: EASE }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link
                to="/work"
                data-testid="hero-view-work-button"
                className="group inline-flex items-center gap-3 bg-accent text-accent-foreground px-8 py-4 text-sm font-medium tracking-wide rounded-full transition-colors duration-300 hover:bg-foreground hover:text-background"
              >
                View work
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-button"
                className="inline-flex items-center gap-3 border border-foreground/25 px-8 py-4 text-sm font-medium tracking-wide rounded-full transition-colors duration-300 hover:border-accent hover:text-accent"
              >
                Get in touch
              </Link>
            </motion.div>
          </motion.div>
          <motion.div
            className="lg:col-span-4"
            style={{ y: parallaxY }}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: EASE }}
          >
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border border-accent/40" />
              {settings?.portrait_url ? (
                <img
                  src={fileUrl(settings.portrait_url)}
                  alt="Twinkle Rajan — Senior Visual & Brand Designer"
                  className="relative aspect-[3/4] w-full object-cover border border-border"
                />
              ) : (
                <Placeholder label="Portrait — coming soon" className="relative aspect-[3/4] border border-border" />
              )}
              <p className="mt-4 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
                Fig. 01 — The designer
              </p>
            </div>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground"
        >
          Scroll
        </motion.div>
      </section>

      {/* NUMBERS */}
      {settings?.numbers?.length > 0 && (
        <section className="border-t border-border" data-testid="numbers-section">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-20">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
              {settings.numbers.map((n, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <p className="font-serif text-4xl sm:text-5xl tracking-tight" data-testid={`number-value-${i}`}>
                    {n.value}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">{n.label}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <Marquee items={SERVICES} />

      {/* FEATURED WORK */}
      <section className="py-20 sm:py-28 lg:py-36" data-testid="featured-work-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal className="flex items-end justify-between mb-16">
            <div>
              <Overline>Selected work</Overline>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
                Featured projects
              </h2>
            </div>
            <Link
              to="/work"
              data-testid="featured-view-all-link"
              className="hidden sm:inline-flex items-center gap-2 text-sm hover:text-accent transition-colors"
            >
              View all <ArrowUpRight size={16} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={(i % 2) * 0.12} className={i % 2 === 1 ? "md:mt-20" : ""}>
                <ProjectCard project={p} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="border-t border-border py-20 sm:py-28" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <Overline>What I do</Overline>
            <h2 className="mt-4 mb-14 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Services
            </h2>
          </Reveal>
          <div>
            {SERVICES.map((s, i) => (
              <Reveal key={s} delay={i * 0.05}>
                <div
                  className="group flex items-baseline gap-6 py-6 border-t border-border last:border-b transition-colors duration-300 hover:bg-accent-soft/40 -mx-4 px-4"
                  data-testid={`service-item-${i}`}
                >
                  <span className="font-mono text-xs text-muted-foreground">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight transition-transform duration-500 group-hover:translate-x-3">
                    {s}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="border-t border-border bg-muted/40 py-20 sm:py-28" data-testid="principles-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <Overline>How I work</Overline>
            <h2 className="mt-4 mb-14 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl">
              Three principles, applied to every brief
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1}>
                <div className="border-t-2 border-accent pt-6" data-testid={`principle-${p.n}`}>
                  <span className="font-mono text-xs text-accent">{p.n}</span>
                  <h3 className="mt-3 font-serif text-2xl tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-14">
            <Link
              to="/about"
              data-testid="principles-about-link"
              className="inline-flex items-center gap-2 text-sm hover:text-accent transition-colors"
            >
              More about how I work <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="py-24 sm:py-32 lg:py-40" data-testid="closing-cta-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 text-center">
          <Reveal>
            <Overline className="block mb-8">Available for senior roles</Overline>
            <h2 className="font-serif text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] max-w-4xl mx-auto">
              Have a complex idea that needs to feel{" "}
              <span className="italic text-accent">inevitable</span>?
            </h2>
            <Link
              to="/contact"
              data-testid="closing-cta-button"
              className="group mt-12 inline-flex items-center gap-3 bg-foreground text-background px-10 py-5 text-sm font-medium tracking-wide rounded-full transition-colors duration-300 hover:bg-accent hover:text-accent-foreground"
            >
              Start a conversation
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
